import { PropsWithChildren, forwardRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Close, Replay } from "@mui/icons-material";
import { TransitionForSlideoutPanel } from "./SlideoutPanelTransition.component";

type P = {
  children?: React.ReactNode;
  isOpen: boolean;
  dialogContents?: JSX.Element;
  dialogTitle: string;
  width?: string;
  height?: string;
  showCloseButton: boolean;
  showDetailToggle?: boolean;
  detailGroupFilterToggle?: JSX.Element;
  primaryButtonTitle?: string;
  secondaryButtonTitle?: string;
  disablePrimaryButton?: boolean;
  disablePrimaryTooltip?: string;
  primaryButtonAction?: () => void;
  secondaryButtonAction?: () => void;
  onClose: () => void;
};

export const SlideoutPanelDialog: React.FC<PropsWithChildren<P>> = forwardRef<HTMLDivElement, P>(
  (props, ref) => {
    const {
      children,
      isOpen,
      dialogTitle,
      width = "35vw",
      height,
      showCloseButton,
      showDetailToggle,
      detailGroupFilterToggle,
      primaryButtonTitle,
      primaryButtonAction,
      secondaryButtonTitle,
      secondaryButtonAction,
      disablePrimaryButton,
      disablePrimaryTooltip,
      onClose
    } = props;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isMobileTablet = useMediaQuery(theme.breakpoints.down("lg"));
    const dialogStyleTop = height || (isMobile ? "60px" : "64px");

    const handlePrimaryButtonAction = () => {
      primaryButtonAction?.();
      onClose();
    };
    const handleSecondaryButtonAction = () => {
      secondaryButtonAction?.();
      onClose();
    };

    return (
      <Dialog
        ref={ref}
        open={isOpen}
        onClose={onClose}
        data-testid="slideout-panel-dialog-parent"
        fullScreen={isMobileTablet}
        transitionDuration={400}
        slots={{ transition: TransitionForSlideoutPanel }}
        slotProps={{
          paper: {
            ["data-testid"]: "slideout-panel-dialog-paper",
            sx: {
              height: `calc(100vh - ${dialogStyleTop})`,
              maxHeight: `calc(100vh - ${dialogStyleTop})`,
              position: "absolute",
              right: 0,
              top: dialogStyleTop,
              margin: 0,
              maxWidth: isMobileTablet ? "none" : width,
              width: isMobileTablet ? "none" : width,
              padding: isMobile ? "24px 24px 80px 24px" : 4, // because of mobile bottom bar, when mobile, padding-bottom is added
              overflowY: "inherit"
            }
          }
        }}
      >
        {/* dialog header */}
        <DialogTitle data-testid="slideout-panel-dialog-header">
          <Grid2
            container
            sx={{ alignItems: "center" }}
            data-testid="slideout-panel-dialog-header-container"
          >
            <Grid2
              size={{ xs: 9 }}
              sx={{ display: "flex", alignItems: "center" }}
              data-testid="slideout-panel-dialog-header-title"
            >
              <Typography variant={"h5"} fontWeight={600}>
                {dialogTitle}
              </Typography>
            </Grid2>

            {/* close the dialog */}
            {showCloseButton && (
              <Grid2 size={"grow"}>
                <IconButton
                  data-testid="close-slideout-panel-btn"
                  onClick={onClose}
                  sx={{ float: "right" }}
                >
                  <Close />
                </IconButton>
              </Grid2>
            )}
          </Grid2>

          {/* show details toggle */}
          {showDetailToggle && (
            <Grid2 sx={{ mt: 1 }} data-testid="slideout-panel-dialog-header-detail-toggle-item">
              {detailGroupFilterToggle}
            </Grid2>
          )}
        </DialogTitle>

        {/* dialog contents */}
        <DialogContent
          data-testid="slideout-panel-dialog-contents"
          // sx={{ padding: isMobile ? 0.5 : 3 }}
          sx={{ padding: 3, overflowX: "hidden" }}
        >
          {children}
        </DialogContent>

        {/* dialog actions */}
        <DialogActions data-testid="slideout-panel-dialog-action">
          {secondaryButtonAction && (
            <Button
              color={"secondary"}
              data-testid={`${secondaryButtonTitle}-btn`}
              onClick={handleSecondaryButtonAction}
              startIcon={<Replay sx={{ transform: "rotate(270deg)" }} />}
            >
              {secondaryButtonTitle}
            </Button>
          )}
          {primaryButtonAction && (
            <Tooltip title={disablePrimaryButton ? disablePrimaryTooltip : ""}>
              <span>
                <Button
                  color={"primary"}
                  disabled={disablePrimaryButton}
                  data-testid={`${primaryButtonTitle}-btn`}
                  onClick={handlePrimaryButtonAction}
                >
                  {primaryButtonTitle}
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);
